/**
 * What happens when nobody answers an approval (R-HL-5), for Settings, Policies: the form model,
 * validation (the hub's own expiryPolicyError), the override merge and the preview of what the
 * agent is told. Pure, so it is unit tested; the live and demo data access is at the end.
 */
import { useEffect, useState } from 'react';
import { DEFAULT_EXPIRY_POLICY, durationText, expiryDenyText, expiryPolicyError, expiryPolicyFor, expirySettingsError } from '@orgus/protocol';
import type { ApprovalExpiryPolicy, ApprovalExpirySettings, ApprovalsState, Id, OnExpiry } from '@orgus/protocol';
import { ApiError, api, apiGet, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useApprovals } from './live.ts';

export const ON_EXPIRY_LABEL: Record<OnExpiry, string> = {
  deny: 'Deny and mark blocked',
  hold: 'Keep waiting, then deny',
};

/** Older agents give up on their own at this many minutes, so hold mode cannot wait longer there. */
export const OLD_AGENT_LIMIT_MINUTES = 15;

export interface PolicyForm {
  timeoutMinutes: string;
  onExpiry: OnExpiry;
  holdMaxMinutes: string;
  remindBeforeMinutes: string;
}

export function formOfPolicy(p: ApprovalExpiryPolicy): PolicyForm {
  return { timeoutMinutes: String(p.timeoutMinutes), onExpiry: p.onExpiry, holdMaxMinutes: String(p.holdMaxMinutes), remindBeforeMinutes: String(p.remindBeforeMinutes) };
}

const whole = (raw: string): number => (/^\s*\d+\s*$/.test(raw) ? Number(raw.trim()) : Number.NaN);

export type PolicyFormResult = { ok: true; policy: ApprovalExpiryPolicy } | { ok: false; error: string };

/** Parse and check a form. In deny mode the hold cap is kept valid (at least the wait) so it never blocks a save. */
export function policyOfForm(f: PolicyForm): PolicyFormResult {
  const timeoutMinutes = whole(f.timeoutMinutes);
  const holdMaxMinutes = whole(f.holdMaxMinutes);
  const remindBeforeMinutes = whole(f.remindBeforeMinutes);
  if (Number.isNaN(timeoutMinutes)) return { ok: false, error: 'Enter the wait as a whole number of minutes.' };
  if (f.onExpiry === 'hold' && Number.isNaN(holdMaxMinutes)) return { ok: false, error: 'Enter the longest wait as a whole number of minutes.' };
  if (Number.isNaN(remindBeforeMinutes)) return { ok: false, error: 'Enter the reminder as a whole number of minutes, or 0 for none.' };
  const policy: ApprovalExpiryPolicy = {
    timeoutMinutes,
    onExpiry: f.onExpiry,
    holdMaxMinutes: f.onExpiry === 'hold' ? holdMaxMinutes : Math.max(Number.isNaN(holdMaxMinutes) ? DEFAULT_EXPIRY_POLICY.holdMaxMinutes : holdMaxMinutes, timeoutMinutes),
    remindBeforeMinutes,
  };
  const err = expiryPolicyError(policy);
  return err ? { ok: false, error: err } : { ok: true, policy };
}

/** A project's override: only the fields that differ from the org default. */
export function overrideOf(org: ApprovalExpiryPolicy, p: ApprovalExpiryPolicy): Partial<ApprovalExpiryPolicy> {
  const out: Partial<ApprovalExpiryPolicy> = {};
  if (p.timeoutMinutes !== org.timeoutMinutes) out.timeoutMinutes = p.timeoutMinutes;
  if (p.onExpiry !== org.onExpiry) out.onExpiry = p.onExpiry;
  if (p.holdMaxMinutes !== org.holdMaxMinutes) out.holdMaxMinutes = p.holdMaxMinutes;
  if (p.remindBeforeMinutes !== org.remindBeforeMinutes) out.remindBeforeMinutes = p.remindBeforeMinutes;
  return out;
}

/** The settings with the org default (projectId null) or one project's policy replaced. An override equal to the default is removed. */
export function settingsWith(s: ApprovalExpirySettings, projectId: Id | null, p: ApprovalExpiryPolicy): Pick<ApprovalExpirySettings, 'org' | 'projects'> {
  if (!projectId) return { org: p, projects: { ...s.projects } };
  const projects = { ...s.projects };
  const o = overrideOf(s.org, p);
  if (Object.keys(o).length) projects[projectId] = o;
  else delete projects[projectId];
  return { org: s.org, projects };
}

export function withoutProject(s: ApprovalExpirySettings, projectId: Id): Pick<ApprovalExpirySettings, 'org' | 'projects'> {
  const projects = { ...s.projects };
  delete projects[projectId];
  return { org: s.org, projects };
}

/** One line for a row: "Deny after 15 minutes. Reminder 5 minutes before." */
export function summaryOf(p: ApprovalExpiryPolicy): string {
  const remind = p.remindBeforeMinutes > 0 ? ` Reminder ${durationText(p.remindBeforeMinutes)} before.` : ' No reminder.';
  if (p.onExpiry === 'hold') return `Escalate after ${durationText(p.timeoutMinutes)}, keep waiting, deny at ${durationText(p.holdMaxMinutes)}.${remind}`;
  return `Deny after ${durationText(p.timeoutMinutes)} and mark blocked.${remind}`;
}

/**
 * What the agent is told when this policy denies a mission session's request. Headless (the
 * default launch: Approve and resume restarts it) unless asked for the terminal variant.
 */
export function previewText(p: ApprovalExpiryPolicy, mode: 'headless' | 'interactive' = 'headless'): string {
  return expiryDenyText(p, { mission: true, resume: mode });
}

/** The line under the preview: what a session open in a terminal is told instead. */
export const TERMINAL_PREVIEW_NOTE = 'A session open in a terminal is told instead that a person will approve it in Orgus and then tell it to continue.';

/**
 * What happens to an unanswered request under the org default, in one true sentence (R-HL-5):
 * the deny time, and for hold the escalation before it. Projects can override it.
 */
export function defaultExpiryText(p: ApprovalExpiryPolicy): string {
  if (p.onExpiry === 'hold') {
    return `By default an unanswered request waits ${durationText(p.timeoutMinutes)}, then Orgus escalates and keeps it waiting; at ${durationText(p.holdMaxMinutes)} it is denied, the session stops and its mission shows as blocked.`;
  }
  return `By default an unanswered request is denied after ${durationText(p.timeoutMinutes)}, the session stops and its mission shows as blocked.`;
}

/** The org default from the approvals slice (the expiry settings, else the old timeoutMinutes). */
export function orgExpiry(s: Partial<ApprovalsState> | null | undefined): ApprovalExpiryPolicy {
  return s?.expiry?.org ?? { ...DEFAULT_EXPIRY_POLICY, ...(s?.timeoutMinutes ? { timeoutMinutes: s.timeoutMinutes } : {}) };
}

/** Project rows: those with an override, by project name. */
export function overrideRows(s: ApprovalExpirySettings, name: (id: Id) => string | undefined): { projectId: Id; name: string; policy: ApprovalExpiryPolicy; override: Partial<ApprovalExpiryPolicy> }[] {
  return Object.entries(s.projects)
    .map(([projectId, override]) => ({ projectId, name: name(projectId) ?? projectId, policy: expiryPolicyFor(s, projectId), override }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export interface ExpiryView {
  /** missing: the hub has no expiry setting (older hub, or no approvals module). */
  phase: 'loading' | 'error' | 'missing' | 'ready';
  settings: ApprovalExpirySettings | null;
  error: UiError | null;
  mock: boolean;
  retry: () => void;
}

/** state.ext.approvals.expiry (kept live by SSE); a hub that has not broadcast it yet is asked once. */
export function useExpirySettings(): ExpiryView {
  const { phase, approvals, mock } = useApprovals();
  const live = approvals?.expiry ?? null;
  const [fetched, setFetched] = useState<ApprovalExpirySettings | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [missing, setMissing] = useState(false);
  const [tries, setTries] = useState(0);
  const needFetch = phase === 'ready' && !live && !mock;
  useEffect(() => {
    if (!needFetch) return;
    const ctrl = new AbortController();
    setError(null);
    apiGet<ApprovalExpirySettings>('/api/approvals/expiry', ctrl.signal).then(
      (s) => setFetched(s),
      (e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError && e.status === 404) setMissing(true);
        else setError(toUiError(e));
      },
    );
    return () => ctrl.abort();
  }, [needFetch, tries]);
  const retry = () => setTries((n) => n + 1);
  const settings = live ?? fetched;
  if (phase === 'loading') return { phase: 'loading', settings: null, error: null, mock, retry };
  if (phase === 'missing' || missing) return { phase: 'missing', settings: null, error: null, mock, retry };
  if (settings) return { phase: 'ready', settings, error: null, mock, retry };
  if (phase === 'error' || error) return { phase: 'error', settings: null, error, mock, retry };
  return { phase: 'loading', settings: null, error: null, mock, retry };
}

/** Save the whole setting. Applies to requests raised after it. */
export async function saveExpiry(next: Pick<ApprovalExpirySettings, 'org' | 'projects'>): Promise<ApprovalExpirySettings> {
  const err = expirySettingsError(next);
  if (err) throw new ApiError({ what: 'The expiry setting was not saved.', why: err, next: 'Fix it and save again.' }, 400);
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (st.mode === 'mock' && cur) {
    await new Promise((r) => setTimeout(r, 300));
    const saved: ApprovalExpirySettings = { ...next, updatedAt: Date.now(), updatedBy: 'you' };
    st.applyExt({ ext: 'approvals', type: 'state', data: { ...cur, expiry: saved, timeoutMinutes: saved.org.timeoutMinutes } satisfies ApprovalsState });
    return saved;
  }
  const saved = await api<ApprovalExpirySettings>('/api/approvals/expiry', { method: 'PUT', body: next });
  const now = useOrgus.getState().ext.approvals as ApprovalsState | undefined;
  if (now) useOrgus.getState().applyExt({ ext: 'approvals', type: 'state', data: { ...now, expiry: saved, timeoutMinutes: saved.org.timeoutMinutes } });
  return saved;
}
